import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TablePagination,
} from '@mui/material';
import {
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  LocationOn as LocationIcon,
  LocalFlorist as CropIcon,
  Category as CategoryIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import api from '../services/api';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const ReferenceDataPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  const [governorates, setGovernorates] = useState<any[]>([]);
  const [allStates, setAllStates] = useState<any[]>([]);
  const [allVillages, setAllVillages] = useState<any[]>([]);
  const [statesByGov, setStatesByGov] = useState<{[key: number]: any[]}>({});
  const [villagesByState, setVillagesByState] = useState<{[key: number]: any[]}>({});
  const [expandedGov, setExpandedGov] = useState<number | null>(null);
  const [expandedState, setExpandedState] = useState<number | null>(null);
  const [loadingStates, setLoadingStates] = useState<{[key: number]: boolean}>({});
  const [loadingVillages, setLoadingVillages] = useState<{[key: number]: boolean}>({});
  const [loadingAllStates, setLoadingAllStates] = useState(false);
  const [loadingAllVillages, setLoadingAllVillages] = useState(false);
  
  const [govPage, setGovPage] = useState(0);
  const [govRowsPerPage, setGovRowsPerPage] = useState(50);
  const [statesPage, setStatesPage] = useState(0);
  const [statesRowsPerPage, setStatesRowsPerPage] = useState(50);
  const [villagesPage, setVillagesPage] = useState(0);
  const [villagesRowsPerPage, setVillagesRowsPerPage] = useState(50);
  
  const [crops, setCrops] = useState<any[]>([]);
  const [usages, setUsages] = useState<any[]>([]);
  const [sampleTypes, setSampleTypes] = useState<any[]>([]);
  const [diseasePests, setDiseasePests] = useState<any[]>([]);
  
  const [geneticStatuses, setGeneticStatuses] = useState<any[]>([]);
  const [collectionSources, setCollectionSources] = useState<any[]>([]);
  const [materials, setMaterials] = useState<any[]>([]);
  const [topographies, setTopographies] = useState<any[]>([]);
  const [locationStatuses, setLocationStatuses] = useState<any[]>([]);
  const [soilTextures, setSoilTextures] = useState<any[]>([]);
  const [soilColors, setSoilColors] = useState<any[]>([]);
  const [hardnesses, setHardnesses] = useState<any[]>([]);
  const [drainages, setDrainages] = useState<any[]>([]);
  const [soilPHs, setSoilPHs] = useState<any[]>([]);
  const [waterPHs, setWaterPHs] = useState<any[]>([]);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [copySuccess, setCopySuccess] = useState<string>('');

  useEffect(() => {
    loadReferenceData();
  }, []);

  const handleCopyCode = (code: string, type: string) => {
    navigator.clipboard.writeText(code).then(() => {
      setCopySuccess(`${type}-${code}`);
      setTimeout(() => setCopySuccess(''), 2000);
    });
  };

  const loadReferenceData = async () => {
    setLoading(true);
    setError('');
    try {
      const [
        formData,
        geneticStatusesRes,
        collectionSourcesRes,
        materialsRes,
        topographiesRes,
        locationStatusesRes,
        soilTexturesRes,
        soilColorsRes,
        hardnessesRes,
        drainagesRes,
        soilPHsRes,
        waterPHsRes,
      ] = await Promise.all([
        api.getSurveyFormData(),
        api.get('/genetic-statuses/'),
        api.get('/collection-sources/'),
        api.get('/materials/'),
        api.get('/topographies/'),
        api.get('/location-statuses/'),
        api.get('/soil-textures/'),
        api.get('/soil-colors/'),
        api.get('/hardnesses/'),
        api.get('/drainages/'),
        api.get('/soil-phs/'),
        api.get('/water-phs/'),
      ]);
      
      setGovernorates(Array.isArray(formData.governorates) ? formData.governorates : []);
      setCrops(Array.isArray(formData.crops) ? formData.crops : []);
      setUsages(Array.isArray(formData.usages) ? formData.usages : []);
      setSampleTypes(Array.isArray(formData.sample_types) ? formData.sample_types : []);
      setDiseasePests(Array.isArray(formData.disease_pests) ? formData.disease_pests : []);
      
      setGeneticStatuses(Array.isArray(geneticStatusesRes.data.results) ? geneticStatusesRes.data.results : []);
      setCollectionSources(Array.isArray(collectionSourcesRes.data.results) ? collectionSourcesRes.data.results : []);
      setMaterials(Array.isArray(materialsRes.data.results) ? materialsRes.data.results : []);
      setTopographies(Array.isArray(topographiesRes.data.results) ? topographiesRes.data.results : []);
      setLocationStatuses(Array.isArray(locationStatusesRes.data.results) ? locationStatusesRes.data.results : []);
      setSoilTextures(Array.isArray(soilTexturesRes.data.results) ? soilTexturesRes.data.results : []);
      setSoilColors(Array.isArray(soilColorsRes.data.results) ? soilColorsRes.data.results : []);
      setHardnesses(Array.isArray(hardnessesRes.data.results) ? hardnessesRes.data.results : []);
      setDrainages(Array.isArray(drainagesRes.data.results) ? drainagesRes.data.results : []);
      setSoilPHs(Array.isArray(soilPHsRes.data.results) ? soilPHsRes.data.results : []);
      setWaterPHs(Array.isArray(waterPHsRes.data.results) ? waterPHsRes.data.results : []);
    } catch (err: any) {
      setError(err.message || t('errorFetchingData'));
    } finally {
      setLoading(false);
    }
  };

  const handleGovernorateClick = async (govId: number) => {
    if (expandedGov === govId) {
      setExpandedGov(null);
      return;
    }
    setExpandedGov(govId);
    
    if (!statesByGov[govId]) {
      setLoadingStates({...loadingStates, [govId]: true});
      try {
        const response = await api.get(`/states/?governorate=${govId}`);
        const newStatesByGov = {...statesByGov};
        const statesData = response.data.results || response.data;
        newStatesByGov[govId] = Array.isArray(statesData) ? statesData : [];
        setStatesByGov(newStatesByGov);
      } catch (err) {
        const newStatesByGov = {...statesByGov};
        newStatesByGov[govId] = [];
        setStatesByGov(newStatesByGov);
      } finally {
        const newLoadingStates = {...loadingStates};
        newLoadingStates[govId] = false;
        setLoadingStates(newLoadingStates);
      }
    }
  };

  const handleStateClick = async (stateId: number) => {
    if (expandedState === stateId) {
      setExpandedState(null);
      return;
    }
    setExpandedState(stateId);
    
    if (!villagesByState[stateId]) {
      setLoadingVillages({...loadingVillages, [stateId]: true});
      try {
        const response = await api.get(`/villages/?state=${stateId}`);
        const newVillagesByState = {...villagesByState};
        const villagesData = response.data.results || response.data;
        newVillagesByState[stateId] = Array.isArray(villagesData) ? villagesData : [];
        setVillagesByState(newVillagesByState);
      } catch (err) {
        const newVillagesByState = {...villagesByState};
        newVillagesByState[stateId] = [];
        setVillagesByState(newVillagesByState);
      } finally {
        const newLoadingVillages = {...loadingVillages};
        newLoadingVillages[stateId] = false;
        setLoadingVillages(newLoadingVillages);
      }
    }
  };

  const filterData = (data: any[], fields: string[]) => {
    if (!searchTerm) return data;
    return data.filter(item =>
      fields.some(field =>
        item[field]?.toString().toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setSearchTerm('');
    
    if (newValue === 2 && allStates.length === 0 && !loadingAllStates) {
      loadAllStates();
    }
    if (newValue === 3 && allVillages.length === 0 && !loadingAllVillages) {
      loadAllVillages();
    }
  };

  const loadAllStates = async () => {
    setLoadingAllStates(true);
    try {
      let allStatesData: any[] = [];
      let nextPage: string | null = '/states/';
      
      while (nextPage) {
        const response: any = await api.get(nextPage);
        const data: any = response.data;
        
        if (data.results) {
          allStatesData = [...allStatesData, ...data.results];
          if (data.next) {
            nextPage = data.next.replace(/^https?:\/\/[^\/]+/, '').replace(/^\/api\//, '/');
          } else {
            nextPage = null;
          }
        } else {
          allStatesData = Array.isArray(data) ? data : [];
          nextPage = null;
        }
      }
      setAllStates(allStatesData);
    } catch (err) {
      setAllStates([]);
    } finally {
      setLoadingAllStates(false);
    }
  };

  const loadAllVillages = async () => {
    setLoadingAllVillages(true);
    try {
      let allVillagesData: any[] = [];
      let nextPage: string | null = '/villages/';
      
      while (nextPage) {
        const response: any = await api.get(nextPage);
        const data: any = response.data;
        
        if (data.results) {
          allVillagesData = [...allVillagesData, ...data.results];
          if (data.next) {
            nextPage = data.next.replace(/^https?:\/\/[^\/]+/, '').replace(/^\/api\//, '/');
          } else {
            nextPage = null;
          }
        } else {
          allVillagesData = Array.isArray(data) ? data : [];
          nextPage = null;
        }
      }
      setAllVillages(allVillagesData);
    } catch (err) {
      setAllVillages([]);
    } finally {
      setLoadingAllVillages(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  const filteredGovernorates = filterData(governorates, ['name_ar', 'name_en', 'code']);
  const filteredAllStates = filterData(allStates, ['name_ar', 'name_en', 'code', 'governorate_name_ar', 'governorate_name_en']);
  const filteredAllVillages = filterData(allVillages, ['name_ar', 'name_en', 'code', 'state_name_ar', 'governorate_name_ar']);
  const filteredCrops = filterData(crops, ['name_ar', 'name_en', 'code', 'scientific_name']);
  const filteredUsages = filterData(usages, ['name_ar', 'name_en']);
  const filteredSampleTypes = filterData(sampleTypes, ['name_ar', 'name_en']);
  const filteredDiseasePests = filterData(diseasePests, ['name_ar', 'name_en', 'type']);
  
  const filteredGeneticStatuses = filterData(geneticStatuses, ['name_ar', 'name_en']);
  const filteredCollectionSources = filterData(collectionSources, ['name_ar', 'name_en']);
  const filteredMaterials = filterData(materials, ['name_ar', 'name_en']);
  const filteredTopographies = filterData(topographies, ['name_ar', 'name_en']);
  const filteredLocationStatuses = filterData(locationStatuses, ['name_ar', 'name_en']);
  const filteredSoilTextures = filterData(soilTextures, ['name_ar', 'name_en']);
  const filteredSoilColors = filterData(soilColors, ['name_ar', 'name_en']);
  const filteredHardnesses = filterData(hardnesses, ['name_ar', 'name_en']);
  const filteredDrainages = filterData(drainages, ['name_ar', 'name_en']);
  const filteredSoilPHs = filterData(soilPHs, ['name_ar', 'name_en']);
  const filteredWaterPHs = filterData(waterPHs, ['name_ar', 'name_en']);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          {t('referenceData')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('referenceDataDescription')}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder={t('searchReferenceData')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 0 } }}
        />
      </Paper>

      <Paper>
        <Tabs value={tabValue} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
          <Tab icon={<LocationIcon />} label={`${t('governoratesTree')} (${filteredGovernorates.length})`} />
          <Tab icon={<LocationIcon />} label={`${t('allGovernorates')} (${filteredGovernorates.length})`} />
          <Tab icon={<LocationIcon />} label={`${t('allStates')} (${loadingAllStates ? '...' : allStates.length})`} />
          <Tab icon={<LocationIcon />} label={`${t('allVillages')} (${loadingAllVillages ? '...' : allVillages.length})`} />
          <Tab icon={<CropIcon />} label={`${t('crops')} (${filteredCrops.length})`} />
          <Tab icon={<CategoryIcon />} label={`${t('usages')} (${filteredUsages.length})`} />
          <Tab icon={<CategoryIcon />} label={`${t('sampleTypes')} (${filteredSampleTypes.length})`} />
          <Tab icon={<CategoryIcon />} label={`${t('diseasesAndPests')} (${filteredDiseasePests.length})`} />
          <Tab icon={<CategoryIcon />} label={`${t('geneticStatuses')} (${filteredGeneticStatuses.length})`} />
          <Tab icon={<CategoryIcon />} label={`${t('collectionSources')} (${filteredCollectionSources.length})`} />
          <Tab icon={<CategoryIcon />} label={`${t('materials')} (${filteredMaterials.length})`} />
          <Tab icon={<CategoryIcon />} label={`${t('topographies')} (${filteredTopographies.length})`} />
          <Tab icon={<CategoryIcon />} label={`${t('locationStatuses')} (${filteredLocationStatuses.length})`} />
          <Tab icon={<CategoryIcon />} label={`${t('soilTextures')} (${filteredSoilTextures.length})`} />
          <Tab icon={<CategoryIcon />} label={`${t('soilColors')} (${filteredSoilColors.length})`} />
          <Tab icon={<CategoryIcon />} label={`${t('hardnesses')} (${filteredHardnesses.length})`} />
          <Tab icon={<CategoryIcon />} label={`${t('drainages')} (${filteredDrainages.length})`} />
          <Tab icon={<CategoryIcon />} label={`${t('soilPHs')} (${filteredSoilPHs.length})`} />
          <Tab icon={<CategoryIcon />} label={`${t('waterPHs')} (${filteredWaterPHs.length})`} />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6">{t('governoratesTree')}</Typography>
            <Typography variant="caption" color="text.secondary">
              {t('clickToViewStatesAndVillages')}
            </Typography>
          </Box>
          
          <Box>
            {filteredGovernorates.map((gov) => (
              <Accordion 
                key={gov.id} 
                expanded={expandedGov === gov.id}
                onChange={() => handleGovernorateClick(gov.id)}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                    <Chip label={gov.code} color="primary" size="small" />
                    <Typography sx={{ flex: 1 }}>
                      {isRTL ? gov.name_ar : gov.name_en}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCopyCode(gov.code, 'gov');
                      }}
                    >
                      <CopyIcon fontSize="small" />
                    </IconButton>
                    {copySuccess === `gov-${gov.code}` && (
                      <Typography variant="caption" color="success.main">✓</Typography>
                    )}
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  {loadingStates[gov.id] ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : (
                    <Box sx={{ pl: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                        {t('states')}: ({statesByGov[gov.id]?.length || 0})
                      </Typography>
                      {!statesByGov[gov.id] || statesByGov[gov.id].length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                          {t('noStatesFound')}
                        </Typography>
                      ) : (
                        <>
                          {statesByGov[gov.id].map((state) => (
                            <Accordion 
                              key={state.id}
                              expanded={expandedState === state.id}
                              onChange={() => handleStateClick(state.id)}
                            >
                              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                                  <Chip label={state.code} color="secondary" size="small" />
                                  <Typography sx={{ flex: 1 }}>
                                    {isRTL ? state.name_ar : state.name_en}
                                  </Typography>
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleCopyCode(state.code, 'state');
                                    }}
                                  >
                                    <CopyIcon fontSize="small" />
                                  </IconButton>
                                  {copySuccess === `state-${state.code}` && (
                                    <Typography variant="caption" color="success.main">✓</Typography>
                                  )}
                                </Box>
                              </AccordionSummary>
                              <AccordionDetails>
                                {loadingVillages[state.id] ? (
                                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                                    <CircularProgress size={20} />
                                  </Box>
                                ) : (
                                  <Box sx={{ pl: 2 }}>
                                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                                      {t('villages')}: ({villagesByState[state.id]?.length || 0})
                                    </Typography>
                                    {!villagesByState[state.id] || villagesByState[state.id].length === 0 ? (
                                      <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                                        {t('noVillagesFound')}
                                      </Typography>
                                    ) : (
                                      <TableContainer sx={{ maxHeight: 400 }}>
                                        <Table size="small">
                                          <TableHead>
                                            <TableRow>
                                              <TableCell><strong>{t('villageCode')}</strong></TableCell>
                                              <TableCell><strong>{t('nameArabic')}</strong></TableCell>
                                              <TableCell><strong>{t('nameEnglish')}</strong></TableCell>
                                              <TableCell align="center"><strong>{t('actions')}</strong></TableCell>
                                            </TableRow>
                                          </TableHead>
                                          <TableBody>
                                            {villagesByState[state.id].map((village) => (
                                              <TableRow key={village.id} hover>
                                                <TableCell>
                                                  <Chip label={village.code} size="small" />
                                                </TableCell>
                                                <TableCell>{village.name_ar || '-'}</TableCell>
                                                <TableCell>{village.name_en || '-'}</TableCell>
                                                <TableCell align="center">
                                                  <IconButton
                                                    size="small"
                                                    onClick={() => handleCopyCode(village.code, 'village')}
                                                  >
                                                    <CopyIcon fontSize="small" />
                                                  </IconButton>
                                                  {copySuccess === `village-${village.code}` && (
                                                    <Typography variant="caption" color="success.main">✓</Typography>
                                                  )}
                                                </TableCell>
                                              </TableRow>
                                            ))}
                                          </TableBody>
                                        </Table>
                                      </TableContainer>
                                    )}
                                  </Box>
                                )}
                              </AccordionDetails>
                            </Accordion>
                          ))}
                        </>
                      )}
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">{t('allGovernorates')}</Typography>
            <Chip label={`${t('total')}: ${filteredGovernorates.length}`} color="primary" />
          </Box>
          
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell><strong>{t('code')}</strong></TableCell>
                  <TableCell><strong>{t('nameArabic')}</strong></TableCell>
                  <TableCell><strong>{t('nameEnglish')}</strong></TableCell>
                  <TableCell align="center"><strong>{t('actions')}</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredGovernorates
                  .slice(govPage * govRowsPerPage, govPage * govRowsPerPage + govRowsPerPage)
                  .map((gov, index) => (
                  <TableRow key={`${gov.id}-${index}`} hover>
                    <TableCell>
                      <Chip label={gov.code} size="small" color="primary" variant="outlined" />
                    </TableCell>
                    <TableCell>{gov.name_ar}</TableCell>
                    <TableCell>{gov.name_en}</TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => handleCopyCode(gov.code, 'governorate')}
                      >
                        <CopyIcon fontSize="small" />
                      </IconButton>
                      {copySuccess === `governorate-${gov.code}` && (
                        <Typography variant="caption" color="success.main">✓</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filteredGovernorates.length}
            page={govPage}
            onPageChange={(e, newPage) => setGovPage(newPage)}
            rowsPerPage={govRowsPerPage}
            onRowsPerPageChange={(e) => {
              setGovRowsPerPage(parseInt(e.target.value, 10));
              setGovPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage={i18n.language === 'ar' ? 'صفوف في الصفحة:' : 'Rows per page:'}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">{t('allStates')}</Typography>
            <Chip label={`${t('total')}: ${filteredAllStates.length}`} color="primary" />
          </Box>
          
          {loadingAllStates ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer sx={{ maxHeight: 600 }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>{t('governorate')}</strong></TableCell>
                      <TableCell><strong>{t('governorateCode')}</strong></TableCell>
                      <TableCell><strong>{t('stateCode')}</strong></TableCell>
                      <TableCell><strong>{t('nameArabic')}</strong></TableCell>
                      <TableCell><strong>{t('nameEnglish')}</strong></TableCell>
                      <TableCell align="center"><strong>{t('actions')}</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredAllStates.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          <Typography color="text.secondary">{t('noStatesFound')}</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredAllStates
                        .slice(statesPage * statesRowsPerPage, statesPage * statesRowsPerPage + statesRowsPerPage)
                        .map((state, index) => (
                        <TableRow key={`${state.id}-${index}`} hover>
                          <TableCell>{state.governorate_name_ar}</TableCell>
                          <TableCell>
                            <Chip label={state.governorate_code} size="small" color="secondary" variant="outlined" />
                          </TableCell>
                          <TableCell>
                            <Chip label={state.code} size="small" color="primary" variant="outlined" />
                          </TableCell>
                          <TableCell>{state.name_ar}</TableCell>
                          <TableCell>{state.name_en}</TableCell>
                          <TableCell align="center">
                            <IconButton
                              size="small"
                              onClick={() => handleCopyCode(state.code, 'state')}
                            >
                              <CopyIcon fontSize="small" />
                            </IconButton>
                            {copySuccess === `state-${state.code}` && (
                              <Typography variant="caption" color="success.main">✓</Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={filteredAllStates.length}
                page={statesPage}
                onPageChange={(e, newPage) => setStatesPage(newPage)}
                rowsPerPage={statesRowsPerPage}
                onRowsPerPageChange={(e) => {
                  setStatesRowsPerPage(parseInt(e.target.value, 10));
                  setStatesPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50, 100]}
                labelRowsPerPage={i18n.language === 'ar' ? 'صفوف في الصفحة:' : 'Rows per page:'}
              />
            </>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">{t('allVillages')}</Typography>
            <Chip label={`${t('total')}: ${filteredAllVillages.length}`} color="primary" />
          </Box>
          
          {loadingAllVillages ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer sx={{ maxHeight: 600 }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>{t('governorate')}</strong></TableCell>
                      <TableCell><strong>{t('state')}</strong></TableCell>
                      <TableCell><strong>{t('governorateCode')}</strong></TableCell>
                      <TableCell><strong>{t('stateCode')}</strong></TableCell>
                      <TableCell><strong>{t('villageCode')}</strong></TableCell>
                      <TableCell><strong>{t('nameArabic')}</strong></TableCell>
                      <TableCell><strong>{t('nameEnglish')}</strong></TableCell>
                      <TableCell align="center"><strong>{t('actions')}</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredAllVillages.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center">
                          <Typography color="text.secondary">{t('noVillagesFound')}</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredAllVillages
                        .slice(villagesPage * villagesRowsPerPage, villagesPage * villagesRowsPerPage + villagesRowsPerPage)
                        .map((village, index) => (
                        <TableRow key={`${village.id}-${index}`} hover>
                          <TableCell>{village.governorate_name_ar}</TableCell>
                          <TableCell>{village.state_name_ar}</TableCell>
                          <TableCell>
                            <Chip label={village.governorate_code} size="small" color="secondary" variant="outlined" />
                          </TableCell>
                          <TableCell>
                            <Chip label={village.state_code} size="small" color="info" variant="outlined" />
                          </TableCell>
                          <TableCell>
                            <Chip label={village.code} size="small" color="primary" variant="outlined" />
                          </TableCell>
                          <TableCell>{village.name_ar}</TableCell>
                          <TableCell>{village.name_en}</TableCell>
                          <TableCell align="center">
                            <IconButton
                              size="small"
                              onClick={() => handleCopyCode(village.code, 'village')}
                            >
                              <CopyIcon fontSize="small" />
                            </IconButton>
                            {copySuccess === `village-${village.code}` && (
                              <Typography variant="caption" color="success.main">✓</Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={filteredAllVillages.length}
                page={villagesPage}
                onPageChange={(e, newPage) => setVillagesPage(newPage)}
                rowsPerPage={villagesRowsPerPage}
                onRowsPerPageChange={(e) => {
                  setVillagesRowsPerPage(parseInt(e.target.value, 10));
                  setVillagesPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50, 100]}
                labelRowsPerPage={i18n.language === 'ar' ? 'صفوف في الصفحة:' : 'Rows per page:'}
              />
            </>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={4}>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">{t('crops')}</Typography>
            <Chip label={`${t('total')}: ${filteredCrops.length}`} color="primary" />
          </Box>
          
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell><strong>{t('code')}</strong></TableCell>
                  <TableCell><strong>{t('nameArabic')}</strong></TableCell>
                  <TableCell><strong>{t('nameEnglish')}</strong></TableCell>
                  <TableCell><strong>{t('scientificName')}</strong></TableCell>
                  <TableCell align="center"><strong>{t('actions')}</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredCrops.map((crop, index) => (
                  <TableRow key={`${crop.id}-${index}`} hover>
                    <TableCell>
                      <Chip label={crop.code} color="primary" size="small" />
                    </TableCell>
                    <TableCell>{crop.name_ar}</TableCell>
                    <TableCell>{crop.name_en}</TableCell>
                    <TableCell>{crop.scientific_name || '-'}</TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => handleCopyCode(crop.code, 'crop')}
                      >
                        <CopyIcon fontSize="small" />
                      </IconButton>
                      {copySuccess === `crop-${crop.code}` && (
                        <Typography variant="caption" color="success.main">✓</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        <TabPanel value={tabValue} index={5}>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">{t('usages')}</Typography>
            <Chip label={`${t('total')}: ${filteredUsages.length}`} color="primary" />
          </Box>
          
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell><strong>ID</strong></TableCell>
                  <TableCell><strong>{t('nameArabic')}</strong></TableCell>
                  <TableCell><strong>{t('nameEnglish')}</strong></TableCell>
                  <TableCell align="center"><strong>{t('actions')}</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUsages.map((usage, index) => (
                  <TableRow key={`${usage.id}-${index}`} hover>
                    <TableCell>
                      <Chip label={usage.id} size="small" color="info" variant="outlined" />
                    </TableCell>
                    <TableCell>{usage.name_ar}</TableCell>
                    <TableCell>{usage.name_en}</TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => handleCopyCode(usage.id.toString(), 'usage')}
                      >
                        <CopyIcon fontSize="small" />
                      </IconButton>
                      {copySuccess === `usage-${usage.id}` && (
                        <Typography variant="caption" color="success.main">✓</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        <TabPanel value={tabValue} index={6}>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">{t('sampleTypes')}</Typography>
            <Chip label={`${t('total')}: ${filteredSampleTypes.length}`} color="primary" />
          </Box>
          
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell><strong>ID</strong></TableCell>
                  <TableCell><strong>{t('nameArabic')}</strong></TableCell>
                  <TableCell><strong>{t('nameEnglish')}</strong></TableCell>
                  <TableCell align="center"><strong>{t('actions')}</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredSampleTypes.map((type, index) => (
                  <TableRow key={`${type.id}-${index}`} hover>
                    <TableCell>
                      <Chip label={type.id} size="small" color="info" variant="outlined" />
                    </TableCell>
                    <TableCell>{type.name_ar}</TableCell>
                    <TableCell>{type.name_en}</TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => handleCopyCode(type.id.toString(), 'sampleType')}
                      >
                        <CopyIcon fontSize="small" />
                      </IconButton>
                      {copySuccess === `sampleType-${type.id}` && (
                        <Typography variant="caption" color="success.main">✓</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        <TabPanel value={tabValue} index={7}>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">{t('diseasesAndPests')}</Typography>
            <Chip label={`${t('total')}: ${filteredDiseasePests.length}`} color="primary" />
          </Box>
          
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell><strong>ID</strong></TableCell>
                  <TableCell><strong>{t('type')}</strong></TableCell>
                  <TableCell><strong>{t('nameArabic')}</strong></TableCell>
                  <TableCell><strong>{t('nameEnglish')}</strong></TableCell>
                  <TableCell align="center"><strong>{t('actions')}</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredDiseasePests.map((item, index) => (
                  <TableRow key={`${item.id}-${index}`} hover>
                    <TableCell>
                      <Chip label={item.id} size="small" color="info" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={item.type === 'disease' ? t('disease') : t('pest')} 
                        color={item.type === 'disease' ? 'error' : 'warning'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{item.name_ar}</TableCell>
                    <TableCell>{item.name_en}</TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => handleCopyCode(item.id.toString(), 'diseasePest')}
                      >
                        <CopyIcon fontSize="small" />
                      </IconButton>
                      {copySuccess === `diseasePest-${item.id}` && (
                        <Typography variant="caption" color="success.main">✓</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        <TabPanel value={tabValue} index={8}>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">{t('geneticStatuses')}</Typography>
            <Chip label={`${t('total')}: ${filteredGeneticStatuses.length}`} color="primary" />
          </Box>
          
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell><strong>ID</strong></TableCell>
                  <TableCell><strong>{t('nameArabic')}</strong></TableCell>
                  <TableCell><strong>{t('nameEnglish')}</strong></TableCell>
                  <TableCell align="center"><strong>{t('actions')}</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredGeneticStatuses.map((item, index) => (
                  <TableRow key={`${item.id}-${index}`} hover>
                    <TableCell>
                      <Chip label={item.id} size="small" color="info" variant="outlined" />
                    </TableCell>
                    <TableCell>{item.name_ar}</TableCell>
                    <TableCell>{item.name_en}</TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => handleCopyCode(item.id.toString(), 'geneticStatus')}
                      >
                        <CopyIcon fontSize="small" />
                      </IconButton>
                      {copySuccess === `geneticStatus-${item.id}` && (
                        <Typography variant="caption" color="success.main">✓</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        <TabPanel value={tabValue} index={9}>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">{t('collectionSources')}</Typography>
            <Chip label={`${t('total')}: ${filteredCollectionSources.length}`} color="primary" />
          </Box>
          
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell><strong>ID</strong></TableCell>
                  <TableCell><strong>{t('nameArabic')}</strong></TableCell>
                  <TableCell><strong>{t('nameEnglish')}</strong></TableCell>
                  <TableCell align="center"><strong>{t('actions')}</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredCollectionSources.map((item, index) => (
                  <TableRow key={`${item.id}-${index}`} hover>
                    <TableCell>
                      <Chip label={item.id} size="small" color="info" variant="outlined" />
                    </TableCell>
                    <TableCell>{item.name_ar}</TableCell>
                    <TableCell>{item.name_en}</TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => handleCopyCode(item.id.toString(), 'collectionSource')}
                      >
                        <CopyIcon fontSize="small" />
                      </IconButton>
                      {copySuccess === `collectionSource-${item.id}` && (
                        <Typography variant="caption" color="success.main">✓</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        <TabPanel value={tabValue} index={10}>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">{t('materials')}</Typography>
            <Chip label={`${t('total')}: ${filteredMaterials.length}`} color="primary" />
          </Box>
          
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell><strong>ID</strong></TableCell>
                  <TableCell><strong>{t('nameArabic')}</strong></TableCell>
                  <TableCell><strong>{t('nameEnglish')}</strong></TableCell>
                  <TableCell align="center"><strong>{t('actions')}</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredMaterials.map((item, index) => (
                  <TableRow key={`${item.id}-${index}`} hover>
                    <TableCell>
                      <Chip label={item.id} size="small" color="info" variant="outlined" />
                    </TableCell>
                    <TableCell>{item.name_ar}</TableCell>
                    <TableCell>{item.name_en}</TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => handleCopyCode(item.id.toString(), 'material')}
                      >
                        <CopyIcon fontSize="small" />
                      </IconButton>
                      {copySuccess === `material-${item.id}` && (
                        <Typography variant="caption" color="success.main">✓</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        <TabPanel value={tabValue} index={11}>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">{t('topographies')}</Typography>
            <Chip label={`${t('total')}: ${filteredTopographies.length}`} color="primary" />
          </Box>
          
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell><strong>ID</strong></TableCell>
                  <TableCell><strong>{t('nameArabic')}</strong></TableCell>
                  <TableCell><strong>{t('nameEnglish')}</strong></TableCell>
                  <TableCell align="center"><strong>{t('actions')}</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredTopographies.map((item, index) => (
                  <TableRow key={`${item.id}-${index}`} hover>
                    <TableCell>
                      <Chip label={item.id} size="small" color="info" variant="outlined" />
                    </TableCell>
                    <TableCell>{item.name_ar}</TableCell>
                    <TableCell>{item.name_en}</TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => handleCopyCode(item.id.toString(), 'topography')}
                      >
                        <CopyIcon fontSize="small" />
                      </IconButton>
                      {copySuccess === `topography-${item.id}` && (
                        <Typography variant="caption" color="success.main">✓</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        <TabPanel value={tabValue} index={12}>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">{t('locationStatuses')}</Typography>
            <Chip label={`${t('total')}: ${filteredLocationStatuses.length}`} color="primary" />
          </Box>
          
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell><strong>ID</strong></TableCell>
                  <TableCell><strong>{t('nameArabic')}</strong></TableCell>
                  <TableCell><strong>{t('nameEnglish')}</strong></TableCell>
                  <TableCell align="center"><strong>{t('actions')}</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredLocationStatuses.map((item, index) => (
                  <TableRow key={`${item.id}-${index}`} hover>
                    <TableCell>
                      <Chip label={item.id} size="small" color="info" variant="outlined" />
                    </TableCell>
                    <TableCell>{item.name_ar}</TableCell>
                    <TableCell>{item.name_en}</TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => handleCopyCode(item.id.toString(), 'locationStatus')}
                      >
                        <CopyIcon fontSize="small" />
                      </IconButton>
                      {copySuccess === `locationStatus-${item.id}` && (
                        <Typography variant="caption" color="success.main">✓</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        <TabPanel value={tabValue} index={13}>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">{t('soilTextures')}</Typography>
            <Chip label={`${t('total')}: ${filteredSoilTextures.length}`} color="primary" />
          </Box>
          
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell><strong>ID</strong></TableCell>
                  <TableCell><strong>{t('nameArabic')}</strong></TableCell>
                  <TableCell><strong>{t('nameEnglish')}</strong></TableCell>
                  <TableCell align="center"><strong>{t('actions')}</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredSoilTextures.map((item, index) => (
                  <TableRow key={`${item.id}-${index}`} hover>
                    <TableCell>
                      <Chip label={item.id} size="small" color="info" variant="outlined" />
                    </TableCell>
                    <TableCell>{item.name_ar}</TableCell>
                    <TableCell>{item.name_en}</TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => handleCopyCode(item.id.toString(), 'soilTexture')}
                      >
                        <CopyIcon fontSize="small" />
                      </IconButton>
                      {copySuccess === `soilTexture-${item.id}` && (
                        <Typography variant="caption" color="success.main">✓</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        <TabPanel value={tabValue} index={14}>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">{t('soilColors')}</Typography>
            <Chip label={`${t('total')}: ${filteredSoilColors.length}`} color="primary" />
          </Box>
          
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell><strong>ID</strong></TableCell>
                  <TableCell><strong>{t('nameArabic')}</strong></TableCell>
                  <TableCell><strong>{t('nameEnglish')}</strong></TableCell>
                  <TableCell align="center"><strong>{t('actions')}</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredSoilColors.map((item, index) => (
                  <TableRow key={`${item.id}-${index}`} hover>
                    <TableCell>
                      <Chip label={item.id} size="small" color="info" variant="outlined" />
                    </TableCell>
                    <TableCell>{item.name_ar}</TableCell>
                    <TableCell>{item.name_en}</TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => handleCopyCode(item.id.toString(), 'soilColor')}
                      >
                        <CopyIcon fontSize="small" />
                      </IconButton>
                      {copySuccess === `soilColor-${item.id}` && (
                        <Typography variant="caption" color="success.main">✓</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        <TabPanel value={tabValue} index={15}>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">{t('hardnesses')}</Typography>
            <Chip label={`${t('total')}: ${filteredHardnesses.length}`} color="primary" />
          </Box>
          
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell><strong>ID</strong></TableCell>
                  <TableCell><strong>{t('nameArabic')}</strong></TableCell>
                  <TableCell><strong>{t('nameEnglish')}</strong></TableCell>
                  <TableCell align="center"><strong>{t('actions')}</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredHardnesses.map((item, index) => (
                  <TableRow key={`${item.id}-${index}`} hover>
                    <TableCell>
                      <Chip label={item.id} size="small" color="info" variant="outlined" />
                    </TableCell>
                    <TableCell>{item.name_ar}</TableCell>
                    <TableCell>{item.name_en}</TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => handleCopyCode(item.id.toString(), 'hardness')}
                      >
                        <CopyIcon fontSize="small" />
                      </IconButton>
                      {copySuccess === `hardness-${item.id}` && (
                        <Typography variant="caption" color="success.main">✓</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        <TabPanel value={tabValue} index={16}>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">{t('drainages')}</Typography>
            <Chip label={`${t('total')}: ${filteredDrainages.length}`} color="primary" />
          </Box>
          
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell><strong>ID</strong></TableCell>
                  <TableCell><strong>{t('nameArabic')}</strong></TableCell>
                  <TableCell><strong>{t('nameEnglish')}</strong></TableCell>
                  <TableCell align="center"><strong>{t('actions')}</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredDrainages.map((item, index) => (
                  <TableRow key={`${item.id}-${index}`} hover>
                    <TableCell>
                      <Chip label={item.id} size="small" color="info" variant="outlined" />
                    </TableCell>
                    <TableCell>{item.name_ar}</TableCell>
                    <TableCell>{item.name_en}</TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => handleCopyCode(item.id.toString(), 'drainage')}
                      >
                        <CopyIcon fontSize="small" />
                      </IconButton>
                      {copySuccess === `drainage-${item.id}` && (
                        <Typography variant="caption" color="success.main">✓</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        <TabPanel value={tabValue} index={17}>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">{t('soilPHs')}</Typography>
            <Chip label={`${t('total')}: ${filteredSoilPHs.length}`} color="primary" />
          </Box>
          
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell><strong>ID</strong></TableCell>
                  <TableCell><strong>{t('nameArabic')}</strong></TableCell>
                  <TableCell><strong>{t('nameEnglish')}</strong></TableCell>
                  <TableCell><strong>الحد الأدنى</strong></TableCell>
                  <TableCell><strong>الحد الأقصى</strong></TableCell>
                  <TableCell align="center"><strong>{t('actions')}</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredSoilPHs.map((item, index) => (
                  <TableRow key={`${item.id}-${index}`} hover>
                    <TableCell>
                      <Chip label={item.id} size="small" color="info" variant="outlined" />
                    </TableCell>
                    <TableCell>{item.name_ar}</TableCell>
                    <TableCell>{item.name_en}</TableCell>
                    <TableCell>
                      <Chip label={item.min_value} size="small" color="success" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Chip label={item.max_value} size="small" color="warning" variant="outlined" />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => handleCopyCode(item.id.toString(), 'soilPH')}
                      >
                        <CopyIcon fontSize="small" />
                      </IconButton>
                      {copySuccess === `soilPH-${item.id}` && (
                        <Typography variant="caption" color="success.main">✓</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        <TabPanel value={tabValue} index={18}>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">{t('waterPHs')}</Typography>
            <Chip label={`${t('total')}: ${filteredWaterPHs.length}`} color="primary" />
          </Box>
          
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell><strong>ID</strong></TableCell>
                  <TableCell><strong>{t('nameArabic')}</strong></TableCell>
                  <TableCell><strong>{t('nameEnglish')}</strong></TableCell>
                  <TableCell><strong>الحد الأدنى</strong></TableCell>
                  <TableCell><strong>الحد الأقصى</strong></TableCell>
                  <TableCell align="center"><strong>{t('actions')}</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredWaterPHs.map((item, index) => (
                  <TableRow key={`${item.id}-${index}`} hover>
                    <TableCell>
                      <Chip label={item.id} size="small" color="info" variant="outlined" />
                    </TableCell>
                    <TableCell>{item.name_ar}</TableCell>
                    <TableCell>{item.name_en}</TableCell>
                    <TableCell>
                      <Chip label={item.min_value} size="small" color="success" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Chip label={item.max_value} size="small" color="warning" variant="outlined" />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => handleCopyCode(item.id.toString(), 'waterPH')}
                      >
                        <CopyIcon fontSize="small" />
                      </IconButton>
                      {copySuccess === `waterPH-${item.id}` && (
                        <Typography variant="caption" color="success.main">✓</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>
      </Paper>
    </Box>
  );
};

export default ReferenceDataPage;
